import { useEffect, useMemo, useContext, useState, useRef } from 'react';

import { useParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { useAppSelector, useAppDispatch } from 'hooks';
import { useGroupedBillingSync } from 'hooks/useGroupedBillingSync';
import { usePolicyGroupId } from 'hooks/usePolicyGroupId';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import {
  Text,
  SimpleGrid,
  Alert,
  AlertIcon,
  AlertDescription,
  Box,
  Badge,
  HStack,
  Icon,
  Skeleton,
} from '@chakra-ui/react';

import { MdAccountBalance } from 'react-icons/md';

import Card from 'components/card/Card';

import {
  PaymentLink,
  AgencyBillingFields,
  GroupedBillingSyncSelector,
} from './components';

import getDateFromString from 'helpers/getDateFromString';
import getRadioYesNo from 'helpers/getRadioYesNo';
import getDateString from 'helpers/getDateString';
import getValidationMessage from 'helpers/getValidationMessage';
import formatEmail from 'helpers/formatEmail';
import isValidEmail from 'helpers/isValidEmail';

import {
  getPaymentMethods,
  getPaymentPeriodOptions,
  getCarrierSettings as getCarrierSettingsHelper,
  calculateInstallments,
  getPaymentDateConfig,
  acceptsDownPaymentForPeriod,
  getPaymentPeriodNotice,
} from './helpers';

import {
  usePaymentCalculations,
  useAgencyBilling,
  useAccountHolders,
} from './hooks';

import { useRequireBilling } from '../LOAInformation';

import type { MethodOfPaymentProps } from './types';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import { apiGetApplicationPayments, apiCalculateInterest } from 'api';

import type {
  APIAccountHolders,
  APILinkedPolicy,
  APIPersonalHomeApplicationForm,
  APIPersonalAutoApplicationForm,
} from 'types/api.types';

const INTEREST_RATE_FEATURE_FLAG = 'enable-interest-rate-enhancements';

const InputValueSkeleton = () => (
  <Skeleton
    position="absolute"
    bottom="10px"
    left="12px"
    right="12px"
    height="20px"
    borderRadius="lg"
    width={70}
  />
);

const SharedMethodOfPayment = ({
  formStateKey,
  sectionKey,
  paymentConfig,
  isDisabled,
  actions,
  components,
}: MethodOfPaymentProps): JSX.Element | null => {
  // Destructure form-specific components
  const { FormInput, DateInput, SelectInput, RadioInput } = components;
  const dispatch = useAppDispatch();
  const { uuid } = useParams<{ uuid: string }>();
  const { validations } = useContext(ApplicationFormContext);
  const policyGroupId = usePolicyGroupId(uuid);
  const [isCalculatingInterest, setIsCalculatingInterest] = useState(false);
  const isInterestRateEnhancementEnabled = useFeatureFlag(
    INTEREST_RATE_FEATURE_FLAG,
  );

  // Fetch payments data
  const { data: paymentsData } = useQuery({
    queryKey: ['application-payments', uuid],
    queryFn: () => apiGetApplicationPayments(uuid!),
    refetchInterval: 5000,
    enabled: !!uuid && !isDisabled,
  });

  const insuranceCompany = useAppSelector((state) => {
    if (formStateKey === 'autoPersonalForm') {
      return state.autoPersonalForm.data.company_and_broker_information
        ?.insurance_company;
    }
    if (formStateKey === 'commercialForm') {
      return state.commercialForm.data.insurance_company;
    }
    return state.homePersonalForm.data.policy_information?.insurance_company;
  });

  const { isBillingSynced, billingSourcePolicyUuid, linkedPolicies } =
    useGroupedBillingSync(uuid, { policyGroupId });

  const isBillingDisabled = isDisabled || isBillingSynced;

  const selectedPaymentPeriod = useAppSelector(
    (state) => state[formStateKey].data.billing?.payment_schedule || '',
  );

  const paymentDate = useAppSelector(
    (state) => state[formStateKey].data.billing?.payment_date,
  );

  const paymentDayOfMonth = useAppSelector(
    (state) => state[formStateKey].data.billing?.payment_day_of_month || '',
  );

  const selectedPaymentMethod = useAppSelector(
    (state) => state[formStateKey].data.billing?.payment_method || '',
  );

  const hasPaymentDetails = Boolean(
    paymentsData?.some(
      (payment: { type_name?: string; section_name?: string }) =>
        payment.type_name === selectedPaymentMethod &&
        payment.section_name === 'payment_method',
    ),
  );

  const hasDownPayment = useAppSelector(
    (state) => state[formStateKey].data.billing?.has_down_payment,
  );

  const downPaymentAmount = useAppSelector(
    (state) => state[formStateKey].data.billing?.down_payment_amount || '',
  );

  const downPaymentDate = useAppSelector(
    (state) => state[formStateKey].data.billing?.down_payment_date || '',
  );

  const downPaymentMethod = useAppSelector(
    (state) => state[formStateKey].data.billing?.down_payment_method || '',
  );

  const effectiveDate = useAppSelector((state) => {
    if (formStateKey === 'commercialForm') {
      return state.commercialForm.data.effective_date;
    }
    return state[formStateKey].data.policy_period?.effective_date;
  });

  const personalApplicant = useAppSelector((state) => {
    if (formStateKey === 'commercialForm') {
      return undefined;
    }
    if (formStateKey === 'autoPersonalForm') {
      return state.autoPersonalForm.data?.applicant;
    }
    return state.homePersonalForm.data?.applicants?.[0];
  });

  const commercialApplicantEmail = useAppSelector(
    (state) => state.commercialForm.data.applicant_email || '',
  );

  const commercialClientName = useAppSelector(
    (state) => state.commercialForm.data.client_name || '',
  );

  const applicant = useMemo(() => {
    if (formStateKey === 'commercialForm') {
      return {
        email: commercialApplicantEmail,
        first_name: commercialClientName,
        middle_name: '',
        last_name: '',
      };
    }
    return personalApplicant;
  }, [
    formStateKey,
    personalApplicant,
    commercialApplicantEmail,
    commercialClientName,
  ]);

  const commercialFirstCoApplicant = useAppSelector((state) =>
    formStateKey === 'commercialForm'
      ? state.commercialForm.data.co_applicants?.[0]
      : undefined,
  );

  const nonCommercialCoApplicant = useAppSelector((state) => {
    if (formStateKey === 'autoPersonalForm') {
      return state.autoPersonalForm.data.co_applicant;
    }
    if (formStateKey === 'homePersonalForm') {
      return state.homePersonalForm.data.applicants?.[1];
    }
    return undefined;
  });

  const coApplicant = useMemo(() => {
    if (formStateKey === 'commercialForm') {
      const first = commercialFirstCoApplicant;
      if (!first || (!first.name && !first.email)) return undefined;
      return { name: first.name, email: first.email };
    }
    return nonCommercialCoApplicant;
  }, [formStateKey, commercialFirstCoApplicant, nonCommercialCoApplicant]);

  const accountHolders = useAppSelector((state) => {
    if (formStateKey === 'autoPersonalForm') {
      return state.autoPersonalForm.data.account_holders;
    }
    if (formStateKey === 'commercialForm') {
      return state.commercialForm.data.account_holders;
    }
    return state.homePersonalForm.data.account_holders;
  }) as APIAccountHolders;

  const methodOfPayment = useAppSelector((state) => {
    if (formStateKey === 'autoPersonalForm') {
      return state.autoPersonalForm.data.method_of_payment;
    }
    if (formStateKey === 'commercialForm') {
      return state.commercialForm.data.method_of_payment;
    }
    return state.homePersonalForm.data.method_of_payment;
  }) as
    | APIPersonalHomeApplicationForm['method_of_payment']
    | APIPersonalAutoApplicationForm['method_of_payment'];

  const isDataSaved = useAppSelector(
    (state) => state[formStateKey].isDataSaved,
  );

  const agencyBillingData = useAppSelector((state) => {
    if (formStateKey === 'autoPersonalForm') {
      return state.autoPersonalForm.data?.agency_billing;
    }
    if (formStateKey === 'commercialForm') {
      return state.commercialForm.data?.agency_billing;
    }
    return state.homePersonalForm.data?.agency_billing;
  });

  // Account holders hook
  const {
    showSecondAccountHolder,
    validateFirstAccountHolderEmail,
    validateSecondAccountHolderEmail,
    getFirstHolderSelectionValue,
    getSecondHolderSelectionValue,
    getFirstHolderOptions,
    getSecondHolderOptions,
    handleFirstHolderSelectionChange,
    handleSecondHolderSelectionChange,
    isSecondHolderSameAsCoApplicant,
  } = useAccountHolders({
    accountHolders,
    applicant,
    coApplicant,
    actions,
  });

  // Helper to apply interest calculation result to form state (when enhancement enabled)
  const applyInterestResult = (result: {
    interest: string;
    interest_amount: string;
  }) => {
    dispatch(
      actions.changeMethodOfPayment({
        name: 'interest',
        value: result.interest,
      }),
    );
    // Auto + Commercial use interest_amount, Home uses additional_charges
    const fieldName =
      formStateKey === 'autoPersonalForm' || formStateKey === 'commercialForm'
        ? 'interest_amount'
        : 'additional_charges';
    dispatch(
      actions.changeMethodOfPayment({
        name: fieldName,
        value: result.interest_amount,
      }),
    );
  };

  const prevBillingSourceRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (prevBillingSourceRef.current === undefined) {
      prevBillingSourceRef.current = billingSourcePolicyUuid;
      return;
    }

    if (prevBillingSourceRef.current === billingSourcePolicyUuid) {
      return;
    }

    prevBillingSourceRef.current = billingSourcePolicyUuid;

    if (!isInterestRateEnhancementEnabled || !insuranceCompany) return;

    const recalculateInterest = async () => {
      const lob =
        formStateKey === 'autoPersonalForm' || formStateKey === 'commercialForm'
          ? 'auto'
          : 'home';
      const premium = methodOfPayment?.estimated_policy_premium || '0';

      let paymentSchedule: string;

      if (billingSourcePolicyUuid) {
        // Synced to another policy - use source policy's payment schedule
        const sourcePolicy = linkedPolicies.find(
          (p) => p.uuid === billingSourcePolicyUuid,
        );
        paymentSchedule = sourcePolicy?.payment_plan || 'annual';
      } else {
        // Using own billing - use current payment schedule
        paymentSchedule = selectedPaymentPeriod || 'annual';
      }

      setIsCalculatingInterest(true);
      try {
        const result = await apiCalculateInterest({
          lob,
          carrier_name: insuranceCompany,
          payment_schedule: paymentSchedule,
          premium,
        });
        applyInterestResult(result);
      } catch (error) {
        console.error(
          'Failed to recalculate interest for billing sync:',
          error,
        );
      } finally {
        setIsCalculatingInterest(false);
      }
    };

    recalculateInterest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingSourcePolicyUuid]);

  // Manual interest input handler (when enhancement disabled - legacy behavior)
  const handleInterestChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value === '') {
      dispatch(actions.changeMethodOfPayment({ name: 'interest', value: '' }));
      return;
    }

    const decimalRegex = /^(\d+\.?\d*|\.\d*)$/;

    if (decimalRegex.test(value)) {
      dispatch(actions.changeMethodOfPayment({ name: 'interest', value }));
    }
  };

  const handlePaymentPeriodChange = async (value: string) => {
    dispatch(actions.changeBilling({ name: 'payment_schedule', value }));
    dispatch(
      actions.changeMethodOfPayment({ name: 'type_of_payment_plan', value }),
    );

    // Clear installment due date if not annual payment
    if (value !== 'annual') {
      dispatch(
        actions.changeMethodOfPayment({
          name: 'instalment_due_date',
          value: '',
        }),
      );
    }

    // Set number of installments based on payment period
    const installments = calculateInstallments(value, carrierSettings);
    dispatch(
      actions.changeMethodOfPayment({
        name: 'no_of_remaining_instalments',
        value: installments.toString(),
      }),
    );

    // Auto-set payment date to effective date when using date input (not day dropdown)
    const paymentDateConfig = getPaymentDateConfig(carrierSettings, value);
    const shouldShowDateInput = paymentDateConfig === null;

    if (shouldShowDateInput && effectiveDate) {
      dispatch(
        actions.changeBilling({ name: 'payment_date', value: effectiveDate }),
      );
    }

    // Remove all billing-related missing fields when payment period changes
    dispatch(actions.removeMissingMandatoryField('Payment Day of Month'));
    dispatch(actions.removeMissingMandatoryField('Payment Date'));
    dispatch(actions.removeMissingMandatoryField('Payment Method'));
    dispatch(actions.removeMissingMandatoryField('Down Payment'));
    dispatch(actions.removeMissingMandatoryField('Down Payment Amount'));
    dispatch(actions.removeMissingMandatoryField('Down Payment Date'));
    dispatch(actions.removeMissingMandatoryField('Down Payment Method'));
    dispatch(actions.removeMissingMandatoryField('Installment Due Date'));

    // Calculate interest based on new payment schedule (only when enhancement enabled)
    if (isInterestRateEnhancementEnabled) {
      const lob =
        formStateKey === 'autoPersonalForm' || formStateKey === 'commercialForm'
          ? 'auto'
          : 'home';
      const premium = methodOfPayment?.estimated_policy_premium || '0';

      setIsCalculatingInterest(true);
      try {
        const result = await apiCalculateInterest({
          lob,
          carrier_name: insuranceCompany || '',
          payment_schedule: value,
          premium,
        });
        applyInterestResult(result);
      } catch (error) {
        console.error('Failed to calculate interest:', error);
      } finally {
        setIsCalculatingInterest(false);
      }
    }
  };

  const handlePaymentDayOfMonthChange = (event: any) => {
    dispatch(
      actions.changeBilling({
        name: 'payment_day_of_month',
        value: event.target.value,
      }),
    );
  };

  const handlePaymentMethodChange = (value: string) => {
    dispatch(actions.changeBilling({ name: 'payment_method', value }));
  };

  const handleDownPaymentChange = (value: string) => {
    dispatch(
      actions.changeBilling({
        name: 'has_down_payment',
        value: value === 'true',
      }),
    );

    // Remove down payment related missing fields when down payment is set to "No"
    if (value === 'false') {
      dispatch(actions.removeMissingMandatoryField('Down Payment Amount'));
      dispatch(actions.removeMissingMandatoryField('Down Payment Date'));
      dispatch(actions.removeMissingMandatoryField('Down Payment Method'));
    }
  };

  const handleDownPaymentAmountChange = (event: any) => {
    const { value } = event.target;

    if (value === '') {
      dispatch(
        actions.changeBilling({
          name: 'down_payment_amount',
          value: '',
        }),
      );
      return;
    }

    const decimalRegex = /^(\d+\.?\d*|\.\d*)$/;

    if (decimalRegex.test(value)) {
      dispatch(
        actions.changeBilling({
          name: 'down_payment_amount',
          value,
        }),
      );
    }
  };

  const handleDownPaymentDateChange = (date: Date) => {
    dispatch(
      actions.changeBilling({
        name: 'down_payment_date',
        value: getDateString(date),
      }),
    );
  };

  const handleDownPaymentMethodChange = (value: string) => {
    dispatch(actions.changeBilling({ name: 'down_payment_method', value }));
  };

  const handleFirstAccountHolderChange = (event: any) => {
    const { name, value } = event.target;

    const formattedValue = name === 'email' ? formatEmail(value) : value;

    dispatch(
      actions.changeAccountHolder({
        holder: 'first',
        name,
        value: formattedValue,
      }),
    );
  };

  const handleSecondAccountHolderChange = (event: any) => {
    const { name, value } = event.target;

    const formattedValue = name === 'email' ? formatEmail(value) : value;

    dispatch(
      actions.changeAccountHolder({
        holder: 'second',
        name,
        value: formattedValue,
      }),
    );
  };

  const handleAmountPaidWithApplicationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;

    if (value === '') {
      dispatch(
        actions.changeMethodOfPayment({
          name: 'amt_paid_with_application',
          value: '',
        }),
      );
      return;
    }

    const decimalRegex = /^(\d+\.?\d*|\.\d*)$/;

    if (decimalRegex.test(value)) {
      dispatch(
        actions.changeMethodOfPayment({
          name: 'amt_paid_with_application',
          value,
        }),
      );
    }
  };

  const handleMethodOfPaymentDateChange = (date: Date) => {
    dispatch(
      actions.changeMethodOfPayment({
        name: 'instalment_due_date',
        value: getDateString(date),
      }),
    );
  };

  const carrierSettings = useMemo(
    () => getCarrierSettingsHelper(insuranceCompany || '', paymentConfig),
    [insuranceCompany, paymentConfig],
  );

  const handleGroupedBillingSourceChange = (
    sourcePolicy: APILinkedPolicy | null,
  ) => {
    if (!sourcePolicy?.payment_plan) return;

    dispatch(
      actions.changeBilling({
        name: 'payment_schedule',
        value: sourcePolicy.payment_plan,
      }),
    );
    dispatch(
      actions.changeMethodOfPayment({
        name: 'type_of_payment_plan',
        value: sourcePolicy.payment_plan,
      }),
    );

    const installments = calculateInstallments(
      sourcePolicy.payment_plan,
      carrierSettings,
    );
    dispatch(
      actions.changeMethodOfPayment({
        name: 'no_of_remaining_instalments',
        value: installments.toString(),
      }),
    );
    dispatch(actions.removeMissingMandatoryField('Payment Period'));
  };

  const hasDownPaymentRequired = useMemo(() => {
    if (
      !insuranceCompany.includes('Definity') &&
      !insuranceCompany.includes('Economical')
    ) {
      return carrierSettings?.down_payment_required;
    }

    // For Economical check status of specific validations
    if (
      getValidationMessage(validations, '107') ||
      getValidationMessage(validations, '058')
    ) {
      return true;
    }

    return false;
  }, [insuranceCompany, carrierSettings, validations]);

  // Automatic payment calculations
  usePaymentCalculations({
    carrierSettings,
    formStateKey,
    changeMethodOfPaymentAction: actions.changeMethodOfPayment,
  });

  const availablePaymentMethods = useMemo(
    () => getPaymentMethods(carrierSettings),
    [carrierSettings],
  );

  const paymentPeriodOptions = useMemo(() => {
    const baseOptions = getPaymentPeriodOptions(availablePaymentMethods);

    if (getValidationMessage(validations, '129')) {
      return baseOptions.filter((option) => option.value === 'annual');
    }

    return baseOptions;
  }, [availablePaymentMethods, validations]);

  const paymentDateConfig = useMemo(() => {
    if (!carrierSettings || !selectedPaymentPeriod) return null;
    return getPaymentDateConfig(carrierSettings, selectedPaymentPeriod);
  }, [carrierSettings, selectedPaymentPeriod]);

  const periodNotice = useMemo(
    () => getPaymentPeriodNotice(carrierSettings, selectedPaymentPeriod),
    [carrierSettings, selectedPaymentPeriod],
  );

  const dayOptions = useMemo(() => {
    if (!paymentDateConfig || !Array.isArray(paymentDateConfig)) return [];

    const options = [];

    // Check if the array contains strings (exact days) or integers (range)
    if (typeof paymentDateConfig[0] === 'string') {
      // If strings, convert to integers and use as exact day options
      paymentDateConfig.forEach((day) => {
        const dayInt = parseInt(day as string, 10);
        options.push({ value: dayInt.toString(), label: dayInt.toString() });
      });
    } else {
      // If integers, treat as range [start, end]
      const [start, end] = paymentDateConfig;
      for (let i = start; i <= end; i++) {
        options.push({ value: i.toString(), label: i.toString() });
      }
    }

    return options;
  }, [paymentDateConfig]);

  const paymentMethodOptions = useMemo(() => {
    if (
      !selectedPaymentPeriod ||
      !availablePaymentMethods[selectedPaymentPeriod]
    ) {
      return [];
    }

    const periodMethods = availablePaymentMethods[selectedPaymentPeriod];
    const options = [] as Array<{ value: string; label: string }>;
    const labels = {
      eft: 'EFT',
      credit_card: 'Credit Card',
      agency_bill: 'Agency Bill',
      direct_bill: 'Direct Bill Invoice',
    };

    Object.entries(periodMethods).forEach(([method, isAvailable]) => {
      if (isAvailable) {
        options.push({
          value: method,
          label: labels[method as keyof typeof labels],
        });
      }
    });

    return options;
  }, [selectedPaymentPeriod, availablePaymentMethods]);

  // Agency Billing detection
  const { isAgencyBillMode, activeAgency } = useAgencyBilling({
    carrierSettings,
    paymentSchedule: selectedPaymentPeriod,
    paymentMethodOptions,
    selectedPaymentMethod,
    changeBillingAction: actions.changeBilling,
  });

  const downPaymentMethodOptions = useMemo(() => {
    if (!carrierSettings?.down_payment_methods) return [];

    const options = [] as Array<{ value: string; label: string }>;
    const labels = {
      credit_card: 'Credit Card',
      e_transfer: 'E-Transfer',
    };

    Object.entries(carrierSettings.down_payment_methods).forEach(
      ([method, isAvailable]) => {
        if (isAvailable) {
          options.push({
            value: method,
            label: labels[method as keyof typeof labels],
          });
        }
      },
    );

    return options;
  }, [carrierSettings]);

  const showDayDropdown = paymentDateConfig && Array.isArray(paymentDateConfig);

  const showDateInput = paymentDateConfig === null;

  const showPaymentMethods =
    (paymentDate || paymentDayOfMonth || isAgencyBillMode) &&
    paymentMethodOptions.length > 0;

  const showDownPayment =
    selectedPaymentMethod &&
    selectedPaymentPeriod !== 'annual' &&
    acceptsDownPaymentForPeriod(carrierSettings, selectedPaymentPeriod);

  useEffect(() => {
    if (paymentsData) {
      dispatch(actions.updatePayments(paymentsData));
    }
  }, [paymentsData, dispatch, actions]);

  // Auto-select down payment when required by carrier
  useEffect(() => {
    if (hasDownPaymentRequired && showDownPayment && hasDownPayment !== true) {
      dispatch(
        actions.changeBilling({ name: 'has_down_payment', value: true }),
      );
    }
  }, [
    hasDownPaymentRequired,
    showDownPayment,
    hasDownPayment,
    dispatch,
    actions,
  ]);

  useEffect(() => {
    if (showSecondAccountHolder) return;
    dispatch(
      actions.removeMissingMandatoryField('Email (Second Account Holder)'),
    );
    dispatch(
      actions.removeMissingMandatoryField('First Name (Second Account Holder)'),
    );
  }, [showSecondAccountHolder, dispatch, actions]);

  useEffect(() => {
    if (!isBillingSynced) return;
    dispatch(actions.removeMissingMandatoryField('Payment Period'));
    dispatch(actions.removeMissingMandatoryField('Payment Day of Month'));
  }, [isBillingSynced, dispatch, actions]);

  // Check if billing is required based on LOA settings
  const requireBilling = useRequireBilling({ formStateKey });

  // Early return if billing is not required
  if (!requireBilling) {
    return null;
  }

  if (!carrierSettings) {
    return (
      <Card mb="20px" id="sectionMethodOfPayment">
        <Text fontWeight="bold" fontSize="xl" mb="20px">
          Method of Payment
        </Text>

        <Alert status="error">
          <AlertIcon />
          <AlertDescription>
            No payment method configured for insurance company{' '}
            <strong>{insuranceCompany}</strong>
          </AlertDescription>
        </Alert>
      </Card>
    );
  }

  return (
    <Card mb="20px" id="sectionMethodOfPayment">
      {/* Grouped Billing Sync Selector */}
      <GroupedBillingSyncSelector
        isDisabled={isDisabled}
        onBillingSourceChange={handleGroupedBillingSourceChange}
        paymentConfig={paymentConfig}
        policyGroupId={policyGroupId}
        currentBinderHasOwnBilling={Boolean(
          selectedPaymentPeriod && selectedPaymentMethod && hasPaymentDetails,
        )}
      />

      {isBillingSynced && (
        <Box>
          <Text fontWeight="bold" fontSize="xl" mb="20px">
            Policy Amounts
          </Text>

          <SimpleGrid columns={4} spacing="20px">
            {/* START: Interest Rate Enhancement - remove else block when flag is removed */}
            {isInterestRateEnhancementEnabled ? (
              <>
                <Box position="relative">
                  <FormInput
                    section={sectionKey}
                    label="Interest"
                    name="interest"
                    value={
                      isCalculatingInterest
                        ? ''
                        : methodOfPayment?.interest || '0'
                    }
                    onChange={() => {}}
                    disabled
                  />
                  {isCalculatingInterest && <InputValueSkeleton />}
                </Box>
                {(formStateKey === 'autoPersonalForm' ||
                  formStateKey === 'commercialForm') && (
                  <Box position="relative">
                    <FormInput
                      section={sectionKey}
                      label="Interest Amount ($)"
                      name="interest_amount"
                      value={
                        isCalculatingInterest
                          ? ''
                          : (methodOfPayment as any)?.interest_amount || '0'
                      }
                      onChange={() => {}}
                      disabled
                    />
                    {isCalculatingInterest && <InputValueSkeleton />}
                  </Box>
                )}
                {formStateKey === 'homePersonalForm' && (
                  <Box position="relative">
                    <FormInput
                      section={sectionKey}
                      label="Additional Charges"
                      name="additional_charges"
                      value={
                        isCalculatingInterest
                          ? ''
                          : (methodOfPayment as any)?.additional_charges || '0'
                      }
                      onChange={() => {}}
                      disabled
                    />
                    {isCalculatingInterest && <InputValueSkeleton />}
                  </Box>
                )}
              </>
            ) : (
              <FormInput
                section={sectionKey}
                label="Interest"
                name="interest"
                value={methodOfPayment?.interest || ''}
                onChange={handleInterestChange}
                disabled={isDisabled}
                required
              />
            )}
            {/* END: Interest Rate Enhancement */}
            <FormInput
              section={sectionKey}
              label="Amount Paid with Application"
              name="amt_paid_with_application"
              value={methodOfPayment?.amt_paid_with_application || ''}
              onChange={handleAmountPaidWithApplicationChange}
              disabled={isDisabled}
            />
          </SimpleGrid>
        </Box>
      )}

      {/* Hide billing fields when synced from another binder */}
      {!isBillingSynced && (
        <>
          <Box>
            <Text fontWeight="bold" fontSize="xl" mb="20px">
              Account Holders
            </Text>

            <Box mb="30px">
              <Box mb="15px">
                <RadioInput
                  section={sectionKey}
                  label=""
                  options={getFirstHolderOptions()}
                  value={getFirstHolderSelectionValue()}
                  onChange={handleFirstHolderSelectionChange}
                  disabled={isBillingDisabled}
                />
              </Box>

              <SimpleGrid columns={4} spacing="20px">
                <Box>
                  <FormInput
                    section={sectionKey}
                    label="Email"
                    name="email"
                    value={accountHolders?.first?.email || ''}
                    onChange={handleFirstAccountHolderChange}
                    disabled={
                      isBillingDisabled ||
                      accountHolders?.first?.is_same_as_applicant ||
                      accountHolders?.first?.is_same_as_co_applicant
                    }
                    required={applicant.email.length > 0}
                  />
                  {validateFirstAccountHolderEmail() && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {validateFirstAccountHolderEmail()}
                    </Text>
                  )}
                  {!isValidEmail(accountHolders.first.email) && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      Please enter a valid email address
                    </Text>
                  )}
                </Box>
                <FormInput
                  section={sectionKey}
                  label="First Name"
                  name="first_name"
                  value={accountHolders?.first?.first_name || ''}
                  onChange={handleFirstAccountHolderChange}
                  disabled={
                    isBillingDisabled ||
                    accountHolders?.first?.is_same_as_applicant ||
                    accountHolders?.first?.is_same_as_co_applicant
                  }
                  required
                />
                <FormInput
                  section={sectionKey}
                  label="Middle Name"
                  name="middle_name"
                  value={accountHolders?.first?.middle_name || ''}
                  onChange={handleFirstAccountHolderChange}
                  disabled={
                    isBillingDisabled ||
                    accountHolders?.first?.is_same_as_applicant ||
                    accountHolders?.first?.is_same_as_co_applicant
                  }
                />
                <FormInput
                  section={sectionKey}
                  label="Last Name"
                  name="last_name"
                  value={accountHolders?.first?.last_name || ''}
                  onChange={handleFirstAccountHolderChange}
                  disabled={
                    isBillingDisabled ||
                    accountHolders?.first?.is_same_as_applicant ||
                    accountHolders?.first?.is_same_as_co_applicant
                  }
                />
              </SimpleGrid>

              <SimpleGrid columns={4} spacing="20px" mt="15px">
                <FormInput
                  section={sectionKey}
                  label="Address"
                  name="address"
                  value={accountHolders?.first?.address || ''}
                  onChange={handleFirstAccountHolderChange}
                  disabled={
                    isBillingDisabled ||
                    accountHolders?.first?.is_same_as_applicant ||
                    accountHolders?.first?.is_same_as_co_applicant
                  }
                  required
                />
                <FormInput
                  section={sectionKey}
                  label="City"
                  name="city"
                  value={accountHolders?.first?.city || ''}
                  onChange={handleFirstAccountHolderChange}
                  disabled={
                    isBillingDisabled ||
                    accountHolders?.first?.is_same_as_applicant ||
                    accountHolders?.first?.is_same_as_co_applicant
                  }
                  required
                />
                <FormInput
                  section={sectionKey}
                  label="Province Code"
                  name="province_code"
                  value={accountHolders?.first?.province_code || ''}
                  onChange={handleFirstAccountHolderChange}
                  disabled={
                    isBillingDisabled ||
                    accountHolders?.first?.is_same_as_applicant ||
                    accountHolders?.first?.is_same_as_co_applicant
                  }
                  required
                />
                <FormInput
                  section={sectionKey}
                  label="Postal Code"
                  name="postal_code"
                  value={accountHolders?.first?.postal_code || ''}
                  onChange={handleFirstAccountHolderChange}
                  disabled={
                    isBillingDisabled ||
                    accountHolders?.first?.is_same_as_applicant ||
                    accountHolders?.first?.is_same_as_co_applicant
                  }
                  required
                />
              </SimpleGrid>

              <SimpleGrid columns={4} spacing="20px" mt="15px">
                <FormInput
                  section={sectionKey}
                  label="Phone"
                  name="phone"
                  value={accountHolders?.first?.phone || ''}
                  onChange={handleFirstAccountHolderChange}
                  disabled={
                    isBillingDisabled ||
                    accountHolders?.first?.is_same_as_applicant ||
                    accountHolders?.first?.is_same_as_co_applicant
                  }
                />
              </SimpleGrid>
            </Box>

            <Box>
              <Text fontWeight="bold" fontSize="md" mb="10px" mt="20px">
                Second Account Holder
              </Text>

              <Box mb="15px">
                <RadioInput
                  section={sectionKey}
                  label=""
                  options={getSecondHolderOptions()}
                  value={getSecondHolderSelectionValue()}
                  onChange={handleSecondHolderSelectionChange}
                  disabled={isBillingDisabled}
                />
              </Box>

              {showSecondAccountHolder && (
                <>
                  <SimpleGrid columns={4} spacing="20px" mt="15px">
                    <Box>
                      <FormInput
                        section={sectionKey}
                        label="Email"
                        subLabel="Second Account Holder"
                        name="email"
                        value={accountHolders?.second?.email || ''}
                        onChange={handleSecondAccountHolderChange}
                        disabled={
                          isBillingDisabled || isSecondHolderSameAsCoApplicant
                        }
                        required={applicant.email.length > 0}
                      />
                      {validateSecondAccountHolderEmail() && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {validateSecondAccountHolderEmail()}
                        </Text>
                      )}
                      {!isValidEmail(accountHolders.second.email) && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          Please enter a valid email address
                        </Text>
                      )}
                    </Box>
                    <FormInput
                      section={sectionKey}
                      label="First Name"
                      subLabel="Second Account Holder"
                      name="first_name"
                      value={accountHolders?.second?.first_name || ''}
                      onChange={handleSecondAccountHolderChange}
                      disabled={
                        isBillingDisabled || isSecondHolderSameAsCoApplicant
                      }
                      required
                    />
                    <FormInput
                      section={sectionKey}
                      label="Middle Name"
                      name="middle_name"
                      value={accountHolders?.second?.middle_name || ''}
                      onChange={handleSecondAccountHolderChange}
                      disabled={
                        isBillingDisabled || isSecondHolderSameAsCoApplicant
                      }
                    />
                    <FormInput
                      section={sectionKey}
                      label="Last Name"
                      subLabel="Second Account Holder"
                      name="last_name"
                      value={accountHolders?.second?.last_name || ''}
                      onChange={handleSecondAccountHolderChange}
                      disabled={
                        isBillingDisabled || isSecondHolderSameAsCoApplicant
                      }
                    />
                  </SimpleGrid>

                  <SimpleGrid columns={4} spacing="20px" mt="15px">
                    <FormInput
                      section={sectionKey}
                      label="Address"
                      name="address"
                      value={accountHolders?.second?.address || ''}
                      onChange={handleSecondAccountHolderChange}
                      disabled={
                        isBillingDisabled || isSecondHolderSameAsCoApplicant
                      }
                    />
                    <FormInput
                      section={sectionKey}
                      label="City"
                      name="city"
                      value={accountHolders?.second?.city || ''}
                      onChange={handleSecondAccountHolderChange}
                      disabled={
                        isBillingDisabled || isSecondHolderSameAsCoApplicant
                      }
                    />
                    <FormInput
                      section={sectionKey}
                      label="Province Code"
                      name="province_code"
                      value={accountHolders?.second?.province_code || ''}
                      onChange={handleSecondAccountHolderChange}
                      disabled={
                        isBillingDisabled || isSecondHolderSameAsCoApplicant
                      }
                    />
                    <FormInput
                      section={sectionKey}
                      label="Postal Code"
                      name="postal_code"
                      value={accountHolders?.second?.postal_code || ''}
                      onChange={handleSecondAccountHolderChange}
                      disabled={
                        isBillingDisabled || isSecondHolderSameAsCoApplicant
                      }
                    />
                  </SimpleGrid>

                  <SimpleGrid columns={4} spacing="20px" mt="15px">
                    <FormInput
                      section={sectionKey}
                      label="Phone"
                      name="phone"
                      value={accountHolders?.second?.phone || ''}
                      onChange={handleSecondAccountHolderChange}
                      disabled={
                        isBillingDisabled || isSecondHolderSameAsCoApplicant
                      }
                    />
                  </SimpleGrid>
                </>
              )}
            </Box>
          </Box>

          <Text fontWeight="bold" fontSize="xl" mb="20px" mt="20px">
            Method of Payment
          </Text>

          <SimpleGrid columns={4} spacing="20px">
            {paymentPeriodOptions.length > 0 && (
              <SelectInput
                section={sectionKey}
                label="Payment Period"
                options={paymentPeriodOptions}
                value={selectedPaymentPeriod || ''}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  handlePaymentPeriodChange(event.target.value)
                }
                placeholder="Select payment period"
                required
                borderColor={
                  methodOfPayment?.type_of_payment_plan ? 'gray.200' : 'red'
                }
                disabled={isBillingDisabled}
              />
            )}

            {selectedPaymentPeriod && showDayDropdown && (
              <SelectInput
                section={sectionKey}
                label="Payment Day of Month"
                options={dayOptions}
                value={paymentDayOfMonth}
                onChange={handlePaymentDayOfMonthChange}
                placeholder="Select day"
                required
                disabled={isBillingDisabled}
              />
            )}

            {selectedPaymentPeriod && showDateInput && (
              <DateInput
                section={sectionKey}
                label="Payment Date"
                date={effectiveDate ? getDateFromString(effectiveDate) : null}
                // this onChange is intentionally left blank as the date is auto-set to effective date
                onChange={() => {}}
                required
                disabled // always disabled as date is auto-set to effective date
              />
            )}

            {showPaymentMethods && (
              <Box>
                <RadioInput
                  section={sectionKey}
                  label="Payment Method"
                  options={paymentMethodOptions}
                  value={selectedPaymentMethod}
                  onChange={handlePaymentMethodChange}
                  required
                  stacked
                  disabled={isBillingDisabled}
                />

                {(selectedPaymentMethod === 'eft' ||
                  selectedPaymentMethod === 'credit_card') &&
                  !isDisabled && (
                    <PaymentLink
                      paymentMethod={
                        selectedPaymentMethod as 'eft' | 'credit_card'
                      }
                      section="payment_method"
                      accountHolderEmail={accountHolders?.first?.email}
                      isDataSaved={isDataSaved}
                      payments={paymentsData}
                    />
                  )}
              </Box>
            )}

            {showDownPayment && (
              <RadioInput
                section={sectionKey}
                label="Down Payment"
                options={[
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ]}
                value={getRadioYesNo(hasDownPayment)}
                onChange={handleDownPaymentChange}
                required
                stacked
                disabled={hasDownPaymentRequired || isBillingDisabled}
              />
            )}

            {hasDownPayment && showDownPayment && (
              <>
                <FormInput
                  section={sectionKey}
                  name="down_payment_amount"
                  label="Down Payment Amount"
                  value={downPaymentAmount}
                  onChange={handleDownPaymentAmountChange}
                  required
                  disabled={isBillingDisabled}
                />

                <DateInput
                  section={sectionKey}
                  label="Down Payment Date"
                  date={
                    downPaymentDate ? getDateFromString(downPaymentDate) : null
                  }
                  onChange={handleDownPaymentDateChange}
                  required
                  disabled={isBillingDisabled}
                />

                {downPaymentMethodOptions.length > 0 && (
                  <Box>
                    <RadioInput
                      section={sectionKey}
                      label="Down Payment Method"
                      options={downPaymentMethodOptions}
                      value={downPaymentMethod}
                      onChange={handleDownPaymentMethodChange}
                      required
                      stacked
                      disabled={isBillingDisabled}
                    />

                    {downPaymentMethod === 'credit_card' && !isDisabled && (
                      <PaymentLink
                        paymentMethod="credit_card"
                        section="down_payment"
                        accountHolderEmail={accountHolders?.first?.email}
                        isDataSaved={isDataSaved}
                        payments={paymentsData}
                      />
                    )}
                  </Box>
                )}
              </>
            )}
          </SimpleGrid>

          <SimpleGrid columns={4} spacing="20px" marginTop={'20px'}>
            {/* START: Interest Rate Enhancement - remove else block when flag is removed */}
            {isInterestRateEnhancementEnabled ? (
              <>
                <Box position="relative">
                  <FormInput
                    section={sectionKey}
                    label="Interest"
                    name="interest"
                    value={
                      isCalculatingInterest
                        ? ''
                        : methodOfPayment?.interest || '0'
                    }
                    onChange={() => {}}
                    disabled
                  />
                  {isCalculatingInterest && <InputValueSkeleton />}
                </Box>
                {(formStateKey === 'autoPersonalForm' ||
                  formStateKey === 'commercialForm') && (
                  <Box position="relative">
                    <FormInput
                      section={sectionKey}
                      label="Interest Amount ($)"
                      name="interest_amount"
                      value={
                        isCalculatingInterest
                          ? ''
                          : (methodOfPayment as any)?.interest_amount || '0'
                      }
                      onChange={() => {}}
                      disabled
                    />
                    {isCalculatingInterest && <InputValueSkeleton />}
                  </Box>
                )}
                {formStateKey === 'homePersonalForm' && (
                  <Box position="relative">
                    <FormInput
                      section={sectionKey}
                      label="Additional Charges"
                      name="additional_charges"
                      value={
                        isCalculatingInterest
                          ? ''
                          : (methodOfPayment as any)?.additional_charges || '0'
                      }
                      onChange={() => {}}
                      disabled
                    />
                    {isCalculatingInterest && <InputValueSkeleton />}
                  </Box>
                )}
              </>
            ) : (
              <FormInput
                section={sectionKey}
                label="Interest"
                name="interest"
                value={methodOfPayment?.interest || ''}
                onChange={handleInterestChange}
                disabled={isDisabled}
                required
              />
            )}
            {/* END: Interest Rate Enhancement */}

            <FormInput
              section={sectionKey}
              label="Amount Paid with Application"
              name="amt_paid_with_application"
              value={methodOfPayment?.amt_paid_with_application || ''}
              onChange={handleAmountPaidWithApplicationChange}
              disabled={isBillingDisabled}
            />

            {selectedPaymentPeriod === 'monthly' && (
              <SelectInput
                section={sectionKey}
                label="Number of Installments"
                options={Array.from({ length: 12 }, (_, i) => ({
                  value: String(i + 1),
                  label: String(i + 1),
                }))}
                value={methodOfPayment?.no_of_remaining_instalments || ''}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                  dispatch(
                    actions.changeMethodOfPayment({
                      name: 'no_of_remaining_instalments',
                      value: event.target.value,
                    }),
                  );
                }}
                placeholder="Select installments"
                disabled={isBillingDisabled}
              />
            )}

            {selectedPaymentPeriod === 'annual' && (
              <DateInput
                section={sectionKey}
                label="Installment Due Date"
                date={
                  methodOfPayment?.instalment_due_date
                    ? getDateFromString(methodOfPayment.instalment_due_date)
                    : null
                }
                onChange={handleMethodOfPaymentDateChange}
                disabled={isBillingDisabled}
                required
              />
            )}
          </SimpleGrid>
          {isAgencyBillMode &&
            selectedPaymentMethod === 'agency_bill' &&
            activeAgency && (
              <>
                <Alert status="info" mt="20px" borderRadius="md">
                  <AlertIcon />
                  <HStack spacing={2}>
                    <Icon as={MdAccountBalance} />
                    <Text fontWeight="bold">{activeAgency.name}</Text>
                    <Badge colorScheme="blue" variant="solid">
                      Active
                    </Badge>
                  </HStack>
                  <AlertDescription ml={4}>
                    {activeAgency.description}
                  </AlertDescription>
                </Alert>
                <AgencyBillingFields
                  isDisabled={isBillingDisabled}
                  agencyBillingData={agencyBillingData}
                  sectionKey={sectionKey}
                  formType={
                    formStateKey === 'autoPersonalForm' ||
                    formStateKey === 'commercialForm'
                      ? 'auto'
                      : 'home'
                  }
                  onFieldChange={(name, value) => {
                    dispatch(actions.changeAgencyBilling({ name, value }));
                  }}
                  components={{
                    FormInput,
                    SelectInput,
                    RadioInput,
                  }}
                />
              </>
            )}
          {periodNotice && (
            <Alert status="info" mt="20px" borderRadius="md">
              <AlertIcon />
              <AlertDescription>{periodNotice}</AlertDescription>
            </Alert>
          )}
        </>
      )}
    </Card>
  );
};

export default SharedMethodOfPayment;
